/**
 * Button Component (UX-16: Button Variant Guidelines)
 *
 * Button Variant Guidelines:
 * - default (Primary): Main actions, primary CTAs (Save, Submit, Create, Continue)
 * - destructive: Delete, Remove, Cancel subscription, irreversible actions
 * - outline (Secondary): Alternative actions (Cancel, Back, secondary options)
 * - secondary: Less prominent actions, tertiary options
 * - ghost: Subtle actions, icon buttons in toolbars
 * - link: Navigation, text-only links styled as buttons
 *
 * Size Guidelines:
 * - sm: Compact UIs, inline actions (height: 36px)
 * - default: Standard button size (height: 40px)
 * - lg: Prominent CTAs, mobile-friendly (height: 48px)
 * - icon: Icon-only buttons, meets touch targets (44x44px)
 *
 * Icon Guidelines:
 * - Icons are automatically sized to h-4 w-4 (16px)
 * - Use lucide-react icons for consistency
 * - Common icons:
 *   - Plus: "New", "Add", "Create"
 *   - Save: "Save", "Submit"
 *   - Trash: "Delete", "Remove"
 *   - ArrowLeft: "Back", "Previous"
 *   - Check: "Confirm", "Approve"
 *   - X: "Cancel", "Close", "Reject"
 *
 * Usage Examples:
 * - Primary action: <Button>Save Changes</Button>
 * - With icon: <Button><Save />Save</Button>
 * - Destructive: <Button variant="destructive"><Trash />Delete</Button>
 * - Cancel/Back: <Button variant="outline">Cancel</Button>
 * - Loading state: <Button disabled>{loading ? <ButtonSpinner>Saving...</ButtonSpinner> : 'Save'}</Button>
 *
 * Accessibility:
 * - Always provide descriptive button text
 * - Use aria-label for icon-only buttons
 * - Disable during loading states
 * - Ensure sufficient color contrast
 */
"use client"

import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        // Small: 36px height - for compact UIs
        sm: "h-9 rounded-md px-3 text-xs",
        // Default: 40px height - standard button size
        default: "h-10 px-4 py-2",
        // Large: 48px height - prominent CTAs and mobile-friendly
        lg: "h-12 rounded-md px-8",
        // Icon: 44px x 44px - meets touch target guidelines (iOS/Android)
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
